import { type UnwrapNestedRefs } from 'vue';
import type { GIMovieList } from 'src/models/geoItem';
import type {
  BothLeftRight,
  CompareModeProps,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';
import type { RemainingMovieRatesByDateRange } from 'src/models/user';

interface Deps {
  getUIPlayState: (lr: LeftRight) => 'playing' | 'paused';
  pauseUI: (lr_: BothLeftRight) => void;
  isMovieSearchResultModeDetail: () => boolean;
  isMovieSearchResultModeArea: () => boolean;
  remainingMovieRatesByDateRanges: () => RemainingMovieRatesByDateRange[] | undefined;
  // 親のmovieLayerMetaはデータマネージャ初期化時にオブジェクトごと差し替わるため、
  // 値ではなくgetterで受け取って呼び出し時点の最新を参照する
  parentMovieLayerMeta: () => CompareModeProps['parentMovieLayerMeta'];
  parentMovieSearch: CompareModeProps['parentMovieSearch'];
  onCurrentMovieListIdChange: (lr: LeftRight) => void;
}

interface MoviePlayerCompareModeDateRangeSearchContext {
  searchMoviesByDateRange: (lr: LeftRight, dateOffset: number) => void;
}

function calcSearchDateRange(
  baseSearchDate: Date,
  today: Date,
  movieRatesByDateRanges: RemainingMovieRatesByDateRange[],
): { dtFrom: Date; dtTo: Date } {
  // 検索基点日を中心とした動画検索範囲(前後7日間、合計2週間分の動画を取得する)
  const baseSearchDateRange = 7;
  // 検索開始日
  let dtFrom = new Date(baseSearchDate.valueOf() - (baseSearchDateRange * 86400 * 1000));
  // 検索終了日
  let dtTo = new Date(baseSearchDate.valueOf() + (baseSearchDateRange * 86400 * 1000));
  for (const [i, e] of movieRatesByDateRanges.entries()) {
    // 区間開始日
    const dateRangeFrom = new Date(today.valueOf() - (e.daysBeforeFrom * 86400 * 1000));
    // 区間終了日
    const dateRangeTo = new Date(today.valueOf() - (e.daysBeforeTo * 86400 * 1000));
    // baseSearchDateが含まれる区間を探す
    if (baseSearchDate < dateRangeFrom || dateRangeTo <= baseSearchDate) continue;

    // baseSearchDateが含まれる区間をi, 1つ前をi+1, 一つ後ろをi-1とする
    // (配列には時間の降順で入っている)
    // 区間iの動画残存率
    const rate1 = e.rate;
    // baseSearchDateRange分の動画量が取得可能と思われる検索範囲
    const searchDateRange = baseSearchDateRange / rate1;
    dtFrom = new Date(baseSearchDate.valueOf() - (searchDateRange * 86400 * 1000));
    dtTo = new Date(baseSearchDate.valueOf() + (searchDateRange * 86400 * 1000));
    // 検索範囲が区間を跨ぐ場合に、
    // baseSearchDateRangeと同等量の動画が取得できるよう検索範囲を補正する
    // 区間開始日が区間i+1とiを跨ぐ場合
    if (dtFrom < dateRangeFrom && i < (movieRatesByDateRanges.length - 1)) {
      // 区間i+1の動画残存率
      const rate2 = movieRatesByDateRanges[i + 1].rate;
      // 区間i内における、基点日から過去側の検索範囲
      const searchDateRangeMsec1 = baseSearchDate.getTime() - dateRangeFrom.getTime();
      // 区間i+1側で検索する範囲
      const searchDateRangeMsec2 =
        ((baseSearchDateRange * 86400 * 1000) - (searchDateRangeMsec1 * rate1)) / rate2;
      // 基点日から過去側の検索範囲全量
      const estimatedSearchDateRangeMsec = searchDateRangeMsec1 + searchDateRangeMsec2;
      dtFrom = new Date(baseSearchDate.valueOf() - estimatedSearchDateRangeMsec);
    }
    // 区間終了日が区間iとi-1を跨ぐ場合
    if (dateRangeTo <= dtTo && i > 0) {
      // 区間i-1の動画残存率
      const rate2 = movieRatesByDateRanges[i - 1].rate;
      // 区間i内における、基点日から未来側の検索範囲
      const searchDateRangeMsec1 = dateRangeTo.getTime() - baseSearchDate.getTime();
      // 区間i-1側で検索する範囲
      const searchDateRangeMsec2 =
        ((baseSearchDateRange * 86400 * 1000) - (searchDateRangeMsec1 * rate1)) / rate2;
      // 基点日から未来側の検索範囲全量
      const estimatedSearchDateRangeMsec = searchDateRangeMsec1 + searchDateRangeMsec2;
      dtTo = new Date(baseSearchDate.valueOf() + estimatedSearchDateRangeMsec);
    }
    break;
  }
  return { dtFrom, dtTo };
}

// useMoviePlayerCompareMode.tsから分割した日付レンジ検索アルゴリズム（区間補正計算）と、
// 検索結果の取得・同一路線#方向によるフィルタ・
// 検索基点日から一番近い動画の選択を行うrefreshMovieLists。
// 動画切替の実行自体はonCurrentMovieListIdChange(オーケストレーター側)に委譲する。
export function useMoviePlayerCompareModeDateRangeSearch(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): MoviePlayerCompareModeDateRangeSearchContext {
  const refreshMovieLists = async(lr: LeftRight) => {
    const playState = deps.getUIPlayState(lr);
    deps.pauseUI(lr);
    state[lr].isMovieReady = false;
    let results: GIMovieList[] = [];
    const mgr = deps.parentMovieLayerMeta()?.manager;
    if (!mgr) return;

    const params = { ...state.movieSearch[lr].params };
    params.dtTo = new Date(params.dtTo.valueOf() + 1 * 86400 * 1000);
    if (deps.isMovieSearchResultModeDetail()) {
      // 検索条件指定による動画検索
      results = await mgr.getResourcesByParams(params);
    } else if (deps.isMovieSearchResultModeArea()) {
      // 範囲指定による動画検索（範囲モードでは areaQueryObj は必ず設定済み）
      const areaQueryObj = deps.parentMovieSearch.areaQueryObj;
      if (!areaQueryObj) return;
      results = await mgr.getResourcesByArea(params, areaQueryObj);
    }
    // 同一路線#方向でしぼる
    const mlCurrent = state[lr].movieList.listSelection.currentMovieList;
    if (!mlCurrent) return;

    const refRoadNameDisp = mlCurrent.startKp.roadNameDisp;
    const refDirection = mlCurrent.startKp.direction;
    results = results.filter(ml => {
      return refRoadNameDisp === ml.startKp.roadNameDisp &&
        refDirection === ml.startKp.direction;
    });
    if (results.length === 0) {
      state.movieSearch[lr].isResultEmpty = true;
      state[lr].movieList.listSelection.currentMovieLists = [];
      return;
    }
    state.movieSearch[lr].isResultEmpty = false;
    // 地図上の見た目を調整
    const isSelected = lr === 'left';
    results = results.map(ml => {
      ml.isSelected = isSelected; // 赤枠
      ml.showTimeTexts = true; // 時間表示
      return ml;
    });
    // 検索基点日から一番近い動画を選択
    const baseSearchDate = state.movieSearch[lr].baseDate;
    let closestMovieList = results[0];
    if (!baseSearchDate || !results[0].startTs) return;

    let dateDiff = Math.abs(baseSearchDate.getTime() - results[0].startTs.getTime());
    for (const ml of results) {
      const currDiff = Math.abs(baseSearchDate.getTime() - (ml.startTs?.getTime() ?? 0));
      if (currDiff < dateDiff) {
        closestMovieList = ml;
        dateDiff = currDiff;
      }
    }
    state[lr].movieList.listSelection.currentMovieLists = results;
    state[lr].movieList.listSelection.currentMovieListId = closestMovieList.id;
    deps.onCurrentMovieListIdChange(lr);
    void (async() => {
      try {
        await state.uiPromise;
        // 元々停止中だった場合は、動画を停止する
        if (playState === 'paused') {
          deps.pauseUI(lr);
        }
        state[lr].isMovieReady = true;
      } catch (e) {
        console.error(e);
      }
    })();
  };
  const searchMoviesByDateRange = (lr: LeftRight, dateOffset: number) => {
    const now = new Date();
    // 現在の日付
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // 基準となる日付 = 現在選択中動画の日付 or 前回の検索基点日
    const baseDate = state.movieSearch[lr].baseDate;
    if (!baseDate) return;

    const movieRatesByDateRanges = deps.remainingMovieRatesByDateRanges();
    if (!movieRatesByDateRanges) return;

    // 検索基点日
    const baseSearchDate = new Date(baseDate.valueOf() + dateOffset * 86400 * 1000);
    state.movieSearch[lr].baseDate = baseSearchDate;

    const { dtFrom, dtTo } = calcSearchDateRange(baseSearchDate, today, movieRatesByDateRanges);

    const reqParams = { ...deps.parentMovieSearch.params };
    reqParams.dtFrom = new Date(dtFrom.getFullYear(), dtFrom.getMonth(), dtFrom.getDate());
    reqParams.dtTo = new Date(dtTo.getFullYear(), dtTo.getMonth(), dtTo.getDate());
    state.movieSearch[lr].params = reqParams;
    refreshMovieLists(lr);
  };

  return { searchMoviesByDateRange };
}
